import {
  transformFormToTwapParams,
  type TwapOrderFormData,
} from '@/features/order/chat/lib/twap-transform'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import type { UnifiedItemValues } from '@/features/order/order-form/lib/unified-order-schema'
import type { SingleHedgeAlgoParams, TwapAlgoParams } from '@/types/generated/algo'
import {
  Denomination,
  OrderSide,
  OrderType,
  type TimeInForce,
} from '@/types/generated/common/trading'
import { type Exchange, type Instrument, type TradeVenue } from '@/types/generated/common/types'
import type { RelativeLimitPrice } from '@/types/generated/execution'
import type { ExecutionBatch } from '@/types/generated/public_api'

type RawValues = {
  items: { amount: number }[]
  price: number
  twapIntervalMinutes: number
  twapFrequencySeconds: number
  twapFrequencyMinSeconds: number
  twapFrequencyMaxSeconds: number
  twapFrequencyMode: 'fixed' | 'random'
  twapOrderSize: number
  twapOrderSizeMin: number
  twapOrderSizeMax: number
  twapOrderSizeMode: 'fixed' | 'random'
}

// 'singleHedge' is the Hedge tab's: it ignores the rows and fires the prebuilt `singleHedge`.
export type TriggerFirePayloadMode = 'simple' | 'twap' | 'limitChase' | 'singleHedge'

export type SimpleOrderExtras = {
  relativePrice?: RelativeLimitPrice
  reduceOnly?: boolean
  postOnly?: boolean
  tif?: TimeInForce
}

export type BuildTriggerFirePayloadArgs = {
  items: UnifiedItemValues[]
  rawValues: RawValues
  accountId: string
  mode: TriggerFirePayloadMode
  isMarketOrder: boolean
  instruments: Instrument[]
  primaryExchange: Exchange
  tradeVenue: TradeVenue
  simpleOrderExtras?: SimpleOrderExtras
  // Required by 'singleHedge' mode, ignored by the others.
  singleHedge?: SingleHedgeAlgoParams
}

function getOptionContractSize(item: UnifiedItemValues, instruments: Instrument[]): number {
  if (item.itemType !== 'option') return 1
  const inst = instruments.find((i) => i.symbol === item.symbol)
  return Math.max(1, Number(inst?.contractSize) || 1)
}

function getDenomination(item: UnifiedItemValues, instruments: Instrument[]): Denomination {
  const baseCurrency = resolveBaseCurrency(instruments, item.symbol, {
    baseCurrency: item.baseCurrency,
  } as Instrument)
  const isBaseAmount = item.itemType === 'option' || item.amountUnit === baseCurrency
  return isBaseAmount ? Denomination.DENOMINATION_BASE : Denomination.DENOMINATION_QUOTE
}

/**
 * The raw TWAP cadence settings shared by the manual TWAP submit and the
 * triggered-TWAP fire payload. `twapIntervalMinutes` actually holds duration in
 * seconds (legacy field name).
 */
export type TwapCadenceSettings = {
  twapIntervalMinutes: number
  twapFrequencySeconds: number
  twapFrequencyMinSeconds: number
  twapFrequencyMaxSeconds: number
  twapFrequencyMode: 'fixed' | 'random'
  twapOrderSize: number
  twapOrderSizeMin: number
  twapOrderSizeMax: number
  twapOrderSizeMode: 'fixed' | 'random'
}

/**
 * Build TwapAlgoParams from one resolved order leg + the shared duration setting.
 * Single source of truth for both the manual TWAP submit and triggered TWAP, so
 * the fired order mirrors the manual one. Always sends a duration — the regular
 * TWAP tab has no UI for an explicit cadence (frequency/order size), so the
 * backend derives slice frequency/size from `quantity` + `durationSeconds`.
 * `exchange`/`tradeVenue` default to the proto's unspecified (0) when omitted,
 * matching the manual path.
 */
export function buildTwapAlgoParams(args: {
  symbol: string
  quantity: string
  side: 'buy' | 'sell'
  amountUnit: string
  accountId: string
  denomination: Denomination
  exchange?: Exchange
  tradeVenue?: TradeVenue
  raw: TwapCadenceSettings
}): TwapAlgoParams {
  const { symbol, quantity, side, amountUnit, accountId, denomination, exchange, tradeVenue, raw } =
    args

  const durationSeconds = raw.twapIntervalMinutes
  if (durationSeconds <= 0) {
    throw new Error('Please enter a duration')
  }

  const formData: TwapOrderFormData = { symbol, quantity, side, unit: amountUnit }

  const params = transformFormToTwapParams(formData, accountId)
  params.durationSeconds = durationSeconds.toString()
  params.cadence = undefined
  params.denomination = denomination
  if (exchange !== undefined) params.exchange = exchange
  if (tradeVenue !== undefined) params.tradeVenue = tradeVenue
  return params
}

export function buildTriggerFirePayload(args: BuildTriggerFirePayloadArgs): ExecutionBatch {
  const {
    items,
    rawValues,
    accountId,
    mode,
    isMarketOrder,
    instruments,
    primaryExchange,
    tradeVenue,
    simpleOrderExtras,
    singleHedge,
  } = args

  if (mode === 'singleHedge') {
    if (!singleHedge) throw new Error('Nothing to hedge')
    return {
      executionCommands: undefined,
      algoCommands: { commands: [{ startAlgo: { accountId, singleHedge } }] },
      cancelAllOrders: undefined,
    }
  }

  if (mode === 'simple') {
    const extras = simpleOrderExtras ?? {}
    const isRelativeLimit = !isMarketOrder && !!extras.relativePrice
    // An absolute-price limit needs a positive price, mirroring the un-triggered
    // submit guard — otherwise a blank/zero price would fire a '0' limit.
    if (!isMarketOrder && !isRelativeLimit && !(rawValues.price > 0)) {
      throw new Error('Please enter a valid price')
    }
    const commands = items.map((item, index) => {
      const amount = rawValues.items[index]?.amount ?? 0
      if (amount <= 0) throw new Error('Please enter a valid amount')
      const effectiveAmount = amount * getOptionContractSize(item, instruments)
      return {
        accountId,
        new: {
          accountId,
          orderSide: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
          symbol: item.symbol,
          quantity: effectiveAmount.toString(),
          orderType: isMarketOrder ? OrderType.ORDER_TYPE_MARKET : OrderType.ORDER_TYPE_LIMIT,
          price: isMarketOrder || isRelativeLimit ? undefined : rawValues.price?.toString(),
          relativePrice: isRelativeLimit ? extras.relativePrice : undefined,
          denomination: getDenomination(item, instruments),
          reduceOnly: extras.reduceOnly || undefined,
          postOnly: isMarketOrder ? undefined : extras.postOnly || undefined,
          tif: isMarketOrder ? undefined : extras.tif,
        },
      }
    })
    return {
      executionCommands: { commands },
      algoCommands: undefined,
      cancelAllOrders: undefined,
    }
  }

  if (mode === 'twap') {
    // Duration OR cadence (frequency + order size) — mirrors the manual TWAP
    // submit via the shared buildTwapAlgoParams helper.
    const commands = items.map((item, index) => {
      const amount = rawValues.items[index]?.amount
      if (!amount || amount <= 0) throw new Error('Please enter a valid amount')

      const effectiveAmount = amount * getOptionContractSize(item, instruments)
      return {
        startAlgo: {
          accountId,
          twap: buildTwapAlgoParams({
            symbol: item.symbol,
            quantity: effectiveAmount.toString(),
            side: item.side,
            amountUnit: item.amountUnit ?? '',
            accountId,
            denomination: getDenomination(item, instruments),
            exchange: primaryExchange,
            tradeVenue,
            raw: rawValues,
          }),
        },
      }
    })

    return {
      executionCommands: undefined,
      algoCommands: { commands },
      cancelAllOrders: undefined,
    }
  }

  // limitChase
  const commands = items.map((item, index) => {
    const amount = rawValues.items[index]?.amount ?? 0
    if (amount <= 0) throw new Error('Please enter a valid amount')
    const effectiveAmount = amount * getOptionContractSize(item, instruments)
    return {
      startAlgo: {
        accountId,
        limitChase: {
          symbol: item.symbol,
          quantity: effectiveAmount.toString(),
          orderSide: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
          accountId,
          exchange: primaryExchange,
          tradeVenue,
          denomination: getDenomination(item, instruments),
          // Advanced-TWAP per-slice knob; absent keeps standalone limit-chase behaviour.
          catchup: undefined,
        },
      },
    }
  })

  return {
    executionCommands: undefined,
    algoCommands: { commands },
    cancelAllOrders: undefined,
  }
}
